import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Table,
  AutoComplete,
  Input,
  Modal,
  Form,
  Button,
  Tag,
  message,
  Space,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Card,
  Statistic
} from 'antd';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined
} from '@ant-design/icons';

import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const API_URL = '/api/despesas';

const Expenses = () => {

  // =======================================================
  // ESTADOS
  // =======================================================

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState(null);
  const [statusFiltro, setStatusFiltro] = useState(null);
  const [periodoFiltro, setPeriodoFiltro] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [form] = Form.useForm();

  // =======================================================
  // FORMATAR DATA
  // =======================================================

  const formatarData = (data) => {

    if (!data) {
      return '-';
    }

    const dataFormatada = dayjs(data);

    if (!dataFormatada.isValid()) {
      return '-';
    }

    return dataFormatada.format('DD/MM/YYYY');
  };

  // =======================================================
  // FORMATAR MOEDA
  // =======================================================

  const formatarMoeda = (valor) => {

    return Number(valor || 0).toLocaleString(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL'
      }
    );
  };

  // =======================================================
  // CARREGAR DESPESAS
  // =======================================================

  const carregarDespesas = async () => {

    try {

      setLoading(true);

      const response = await fetch(API_URL);

      if (!response.ok) {

        let errorMessage =
          `Erro HTTP: ${response.status}`;

        try {

          const errorData =
            await response.json();

          if (errorData?.message) {
            errorMessage =
              errorData.message;
          }

        } catch {
          // resposta sem JSON
        }

        throw new Error(errorMessage);
      }

      const despesas =
        await response.json();

      if (!Array.isArray(despesas)) {

        throw new Error(
          'A API retornou um formato inválido.'
        );
      }

      setData(despesas);

    } catch (error) {

      console.error(
        'Erro ao carregar despesas:',
        error
      );

      message.error(
        error.message ||
        'Não foi possível carregar as despesas.'
      );

    } finally {

      setLoading(false);

    }
  };

  // =======================================================
  // CARREGAR AO ABRIR A TELA
  // =======================================================

  useEffect(() => {

    carregarDespesas();

  }, []);

  // =======================================================
  // FILTROS
  // =======================================================

  const filteredData = useMemo(() => {

    return data.filter(item => {

      const search =
        searchValue
          .toLowerCase()
          .trim();

      const descricao =
        String(item.descricao || '')
          .toLowerCase();

      const categoria =
        String(item.categoria || '')
          .toLowerCase();

      const matchesSearch =
        !search ||
        descricao.includes(search) ||
        categoria.includes(search);

      const matchesTipo =
        !tipoFiltro ||
        item.tipo === tipoFiltro;

      const matchesStatus =
        !statusFiltro ||
        item.status === statusFiltro;

      let matchesPeriodo = true;

      if (
        periodoFiltro &&
        periodoFiltro.length === 2 &&
        periodoFiltro[0] &&
        periodoFiltro[1]
      ) {

        const dataVencimento =
          dayjs(item.vencimento);

        if (!dataVencimento.isValid()) {

          matchesPeriodo = false;

        } else {

          const dataInicial =
            periodoFiltro[0]
              .startOf('day');

          const dataFinal =
            periodoFiltro[1]
              .endOf('day');

          matchesPeriodo =
            (
              dataVencimento.isSame(
                dataInicial,
                'day'
              ) ||
              dataVencimento.isAfter(
                dataInicial
              )
            ) &&
            (
              dataVencimento.isSame(
                dataFinal,
                'day'
              ) ||
              dataVencimento.isBefore(
                dataFinal
              )
            );
        }
      }

      return (
        matchesSearch &&
        matchesTipo &&
        matchesStatus &&
        matchesPeriodo
      );

    });

  }, [
    data,
    searchValue,
    tipoFiltro,
    statusFiltro,
    periodoFiltro
  ]);

  // =======================================================
  // RESUMOS
  // =======================================================

  const totalDespesas = useMemo(() => {

    return data.reduce(
      (total, item) =>
        total + Number(item.valor || 0),
      0
    );

  }, [data]);

  const totalFixas = useMemo(() => {

    return data
      .filter(item => item.tipo === 'FIXA')
      .reduce(
        (total, item) =>
          total + Number(item.valor || 0),
        0
      );

  }, [data]);

  const totalVariaveis = useMemo(() => {

    return data
      .filter(item => item.tipo === 'VARIAVEL')
      .reduce(
        (total, item) =>
          total + Number(item.valor || 0),
        0
      );

  }, [data]);

  const totalPendentes = useMemo(() => {

    return data
      .filter(item => item.status === 'PENDENTE')
      .reduce(
        (total, item) =>
          total + Number(item.valor || 0),
        0
      );

  }, [data]);

  // =======================================================
  // NOVA DESPESA
  // =======================================================

  const abrirNovaDespesa = () => {

    setEditingExpense(null);

    form.resetFields();

    form.setFieldsValue({
      tipo: 'FIXA',
      status: 'PENDENTE',
      vencimento: null,
      valor: null
    });

    setIsModalVisible(true);
  };

  // =======================================================
  // EDITAR DESPESA
  // =======================================================

  const editarDespesa = (despesa) => {

    setEditingExpense(despesa);

    form.setFieldsValue({

      descricao:
        despesa.descricao,

      categoria:
        despesa.categoria,

      tipo:
        despesa.tipo,

      vencimento:
        despesa.vencimento
          ? dayjs(despesa.vencimento)
          : null,

      valor:
        Number(despesa.valor || 0),

      status:
        despesa.status

    });

    setIsModalVisible(true);
  };

  // =======================================================
  // SALVAR DESPESA
  // =======================================================

  const salvarDespesa = async () => {

    try {

      const values =
        await form.validateFields();

      setSaving(true);

      // ---------------------------------------------------
      // DATA
      // ---------------------------------------------------

      const vencimento =
        values.vencimento
          ? dayjs(values.vencimento)
              .format('YYYY-MM-DD')
          : null;

      // ---------------------------------------------------
      // PAYLOAD
      // ---------------------------------------------------

      const payload = {

        descricao:
          String(values.descricao || '')
            .trim(),

        categoria:
          values.categoria,

        tipo:
          values.tipo,

        vencimento,

        valor:
          Number(values.valor || 0),

        status:
          values.status || 'PENDENTE'

      };

      // ---------------------------------------------------
      // VALIDAÇÕES
      // ---------------------------------------------------

      if (!payload.descricao) {

        message.error(
          'Informe a descrição da despesa.'
        );

        return;
      }

      if (!payload.categoria) {

        message.error(
          'Informe a categoria da despesa.'
        );

        return;
      }

      if (!payload.vencimento) {

        message.error(
          'Informe o vencimento da despesa.'
        );

        return;
      }

      if (
        !Number.isFinite(payload.valor) ||
        payload.valor < 0
      ) {

        message.error(
          'Informe um valor válido.'
        );

        return;
      }

      // ===================================================
      // EDITAR
      // ===================================================

      if (editingExpense) {

        const response =
          await fetch(
            `${API_URL}/${editingExpense.id}`,
            {
              method: 'PUT',

              headers: {
                'Content-Type':
                  'application/json'
              },

              body:
                JSON.stringify(payload)
            }
          );

        if (!response.ok) {

          let errorMessage =
            `Erro HTTP: ${response.status}`;

          try {

            const errorData =
              await response.json();

            if (errorData?.message) {
              errorMessage =
                errorData.message;
            }

          } catch {
            // sem JSON
          }

          throw new Error(errorMessage);
        }

        const despesaAtualizada =
          await response.json();

        setData(prev =>
          prev.map(item =>
            item.id === editingExpense.id
              ? despesaAtualizada
              : item
          )
        );

        message.success(
          'Despesa atualizada com sucesso!'
        );

      }

      // ===================================================
      // NOVA
      // ===================================================

      else {

        const response =
          await fetch(
            API_URL,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json'
              },

              body:
                JSON.stringify(payload)
            }
          );

        if (!response.ok) {

          let errorMessage =
            `Erro HTTP: ${response.status}`;

          try {

            const errorData =
              await response.json();

            if (errorData?.message) {
              errorMessage =
                errorData.message;
            }

          } catch {
            // sem JSON
          }

          throw new Error(errorMessage);
        }

        const novaDespesa =
          await response.json();

        setData(prev => [
          novaDespesa,
          ...prev
        ]);

        message.success(
          'Despesa cadastrada com sucesso!'
        );
      }

      // ===================================================
      // FINALIZAR
      // ===================================================

      setIsModalVisible(false);

      form.resetFields();

      setEditingExpense(null);

    } catch (error) {

      console.error(
        'Erro ao salvar despesa:',
        error
      );

      // Erro de validação do Ant Design
      if (error?.errorFields) {
        return;
      }

      message.error(
        error.message ||
        'Não foi possível salvar a despesa.'
      );

    } finally {

      setSaving(false);

    }
  };

  // =======================================================
  // EXCLUIR DESPESA
  // =======================================================

  const excluirDespesa = (despesa) => {

    Modal.confirm({

      title:
        'Excluir despesa?',

      content: (
        <>
          Esta ação é{' '}
          <b
            style={{
              color: 'red'
            }}
          >
            irreversível
          </b>
          .
          <br />
          Deseja excluir{' '}
          <b>
            {despesa.descricao}
          </b>
          ?
        </>
      ),

      okText:
        'Sim, excluir',

      okType:
        'danger',

      cancelText:
        'Cancelar',

      async onOk() {

        try {

          const response =
            await fetch(
              `${API_URL}/${despesa.id}`,
              {
                method: 'DELETE'
              }
            );

          if (!response.ok) {

            let errorMessage =
              `Erro HTTP: ${response.status}`;

            try {

              const errorData =
                await response.json();

              if (errorData?.message) {
                errorMessage =
                  errorData.message;
              }

            } catch {
              // sem JSON
            }

            throw new Error(
              errorMessage
            );
          }

          setData(prev =>
            prev.filter(
              item =>
                item.id !== despesa.id
            )
          );

          message.success(
            'Despesa excluída com sucesso!'
          );

        } catch (error) {

          console.error(
            'Erro ao excluir despesa:',
            error
          );

          message.error(
            error.message ||
            'Não foi possível excluir a despesa.'
          );

          throw error;
        }
      }

    });
  };

  // =======================================================
  // COLUNAS
  // =======================================================

  const columns = [

    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',

      render: descricao =>
        descricao || '-'
    },

    {
      title: 'Categoria',
      dataIndex: 'categoria',
      key: 'categoria',

      render: categoria =>
        categoria || '-'
    },

    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',

      render: tipo => (

        <Tag
          color={
            tipo === 'FIXA'
              ? 'blue'
              : 'orange'
          }
        >
          {
            tipo === 'FIXA'
              ? 'Fixa'
              : 'Variável'
          }
        </Tag>

      )
    },

    {
      title: 'Vencimento',
      dataIndex: 'vencimento',
      key: 'vencimento',

      render: data =>
        formatarData(data)
    },

    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',

      align: 'right',

      render: valor =>
        formatarMoeda(valor)
    },

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',

      render: status => {

        const config = {

          PAGA: {
            color: 'green',
            label: 'Paga'
          },

          PENDENTE: {
            color: 'orange',
            label: 'Pendente'
          },

          CANCELADA: {
            color: 'red',
            label: 'Cancelada'
          }

        };

        return (

          <Tag
            color={
              config[status]?.color ||
              'default'
            }
          >
            {
              config[status]?.label ||
              status ||
              '-'
            }
          </Tag>

        );

      }

    },

    {
      title: 'Ações',
      key: 'acao',

      fixed: 'right',

      render: (_, despesa) => (

        <Space>

          <Button
            type="link"
            icon={
              <EditOutlined />
            }
            onClick={() =>
              editarDespesa(despesa)
            }
          >
            Editar
          </Button>

          <Button
            danger
            type="link"
            icon={
              <DeleteOutlined />
            }
            onClick={() =>
              excluirDespesa(despesa)
            }
          >
            Excluir
          </Button>

        </Space>

      )
    }

  ];

  // =======================================================
  // RENDER
  // =======================================================

  return (

    <div>

      {/* =================================================
          RESUMOS
      ================================================= */}

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 20
        }}
      >

        <Col
          xs={24}
          sm={12}
          md={6}
        >

          <Card>

            <Statistic
              title="Total de Despesas"
              value={totalDespesas}
              precision={2}
              prefix="R$"
              valueStyle={{
                color: '#1677ff'
              }}
            />

          </Card>

        </Col>

        <Col
          xs={24}
          sm={12}
          md={6}
        >

          <Card>

            <Statistic
              title="Despesas Fixas"
              value={totalFixas}
              precision={2}
              prefix="R$"
              valueStyle={{
                color: '#1677ff'
              }}
            />

          </Card>

        </Col>

        <Col
          xs={24}
          sm={12}
          md={6}
        >

          <Card>

            <Statistic
              title="Despesas Variáveis"
              value={totalVariaveis}
              precision={2}
              prefix="R$"
              valueStyle={{
                color: '#fa8c16'
              }}
            />

          </Card>

        </Col>

        <Col
          xs={24}
          sm={12}
          md={6}
        >

          <Card>

            <Statistic
              title="Pendentes"
              value={totalPendentes}
              precision={2}
              prefix="R$"
              valueStyle={{
                color: '#cf1322'
              }}
            />

          </Card>

        </Col>

      </Row>

      {/* =================================================
          FILTROS
      ================================================= */}

      <Space
        style={{
          marginBottom: 15,
          width: '100%',
          flexWrap: 'wrap'
        }}
      >

        <AutoComplete
          style={{
            width: 300
          }}
          value={searchValue}
          onChange={
            setSearchValue
          }
        >

          <Input
            prefix={
              <SearchOutlined />
            }
            placeholder="Buscar despesa"
            size="large"
            allowClear
          />

        </AutoComplete>

        <Select
          placeholder="Tipo"
          allowClear
          size="large"
          style={{
            width: 150
          }}
          value={tipoFiltro}
          onChange={
            setTipoFiltro
          }
        >

          <Option value="FIXA">
            Fixa
          </Option>

          <Option value="VARIAVEL">
            Variável
          </Option>

        </Select>

        <Select
          placeholder="Status"
          allowClear
          size="large"
          style={{
            width: 160
          }}
          value={statusFiltro}
          onChange={
            setStatusFiltro
          }
        >

          <Option value="PAGA">
            Paga
          </Option>

          <Option value="PENDENTE">
            Pendente
          </Option>

          <Option value="CANCELADA">
            Cancelada
          </Option>

        </Select>

        <RangePicker
          size="large"
          format="DD/MM/YYYY"
          value={periodoFiltro}
          onChange={
            setPeriodoFiltro
          }
          placeholder={[
            'Data inicial',
            'Data final'
          ]}
        />

        <Button
          type="primary"
          size="large"
          icon={
            <PlusOutlined />
          }
          onClick={
            abrirNovaDespesa
          }
        >
          Nova Despesa
        </Button>

      </Space>

      {/* =================================================
          TABELA
      ================================================= */}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [
            '10',
            '20',
            '50',
            '100'
          ],
          showTotal: total =>
            `${total} despesa${
              total !== 1
                ? 's'
                : ''
            }`
        }}
        scroll={{
          x: 900
        }}
      />

      {/* =================================================
          MODAL
      ================================================= */}

      <Modal

        title={
          editingExpense
            ? 'Editar Despesa'
            : 'Nova Despesa'
        }

        open={
          isModalVisible
        }

        onOk={
          salvarDespesa
        }

        onCancel={() => {

          if (saving) {
            return;
          }

          setIsModalVisible(
            false
          );

          form.resetFields();

          setEditingExpense(
            null
          );

        }}

        okText="Salvar"

        cancelText="Cancelar"

        confirmLoading={
          saving
        }

        width={600}

        maskClosable={
          !saving
        }

        keyboard={
          !saving
        }

      >

        <Form
          form={form}
          layout="vertical"
        >

          {/* DESCRIÇÃO */}

          <Form.Item

            label="Descrição"

            name="descricao"

            rules={[
              {
                required: true,
                whitespace: true,
                message:
                  'Informe a descrição da despesa'
              }
            ]}
          >

            <Input
              placeholder="Ex.: Conta de energia"
              maxLength={255}
              showCount
            />

          </Form.Item>

          <Row gutter={16}>

            {/* CATEGORIA */}

            <Col
              xs={24}
              md={12}
            >

              <Form.Item

                label="Categoria"

                name="categoria"

                rules={[
                  {
                    required: true,
                    message:
                      'Informe a categoria'
                  }
                ]}
              >

                <Select
                  placeholder="Selecione a categoria"
                >

                  <Option value="Energia">
                    Energia
                  </Option>

                  <Option value="Internet">
                    Internet
                  </Option>

                  <Option value="Aluguel">
                    Aluguel
                  </Option>

                  <Option value="Embalagens">
                    Embalagens
                  </Option>

                  <Option value="Manutenção">
                    Manutenção
                  </Option>

                  <Option value="Outros">
                    Outros
                  </Option>

                </Select>

              </Form.Item>

            </Col>

            {/* TIPO */}

            <Col
              xs={24}
              md={12}
            >

              <Form.Item

                label="Tipo"

                name="tipo"

                rules={[
                  {
                    required: true,
                    message:
                      'Selecione o tipo'
                  }
                ]}
              >

                <Select>

                  <Option value="FIXA">
                    Despesa Fixa
                  </Option>

                  <Option value="VARIAVEL">
                    Despesa Variável
                  </Option>

                </Select>

              </Form.Item>

            </Col>

          </Row>

          <Row gutter={16}>

            {/* VENCIMENTO */}

            <Col
              xs={24}
              md={12}
            >

              <Form.Item

                label="Vencimento"

                name="vencimento"

                rules={[
                  {
                    required: true,
                    message:
                      'Informe o vencimento'
                  }
                ]}
              >

                <DatePicker

                  style={{
                    width: '100%'
                  }}

                  format="DD/MM/YYYY"

                  placeholder="DD/MM/AAAA"

                  allowClear

                />

              </Form.Item>

            </Col>

            {/* VALOR */}

            <Col
              xs={24}
              md={12}
            >

              <Form.Item

                label="Valor"

                name="valor"

                rules={[
                  {
                    required: true,
                    message:
                      'Informe o valor'
                  },
                  {
                    validator:
                      (_, value) => {

                        if (
                          value === null ||
                          value === undefined
                        ) {

                          return Promise.reject(
                            new Error(
                              'Informe o valor'
                            )
                          );
                        }

                        if (
                          Number(value) < 0
                        ) {

                          return Promise.reject(
                            new Error(
                              'O valor não pode ser negativo'
                            )
                          );
                        }

                        return Promise.resolve();
                      }
                  }
                ]}
              >

                <InputNumber

                  style={{
                    width: '100%'
                  }}

                  min={0}

                  precision={2}

                  decimalSeparator=","

                  placeholder="0,00"

                  controls={false}

                  prefix="R$"

                />

              </Form.Item>

            </Col>

          </Row>

          {/* STATUS */}

          <Form.Item
            label="Status"
            name="status"
            rules={[
              {
                required: true,
                message:
                  'Selecione o status'
              }
            ]}
          >

            <Select>

              <Option value="PENDENTE">
                Pendente
              </Option>

              <Option value="PAGA">
                Paga
              </Option>

              <Option value="CANCELADA">
                Cancelada
              </Option>

            </Select>

          </Form.Item>

        </Form>

      </Modal>

    </div>

  );
};

export default Expenses;